import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useCookies } from 'react-cookie'

import { setUser, setToken } from '../authentication/authenticationSlice'

import useValidator from '../authentication/useValidator'

import api, { isHTTPError } from '../api'

import produce from 'immer'
import { setOrganizations } from '../organizations/organizationsSlice'
import useStorage from 'containers/useStorage'
import { getSignupAttribution } from 'utils/signupAttribution'

const useSignUp = () => {
  const dispatch = useDispatch()
  const [, setCookie] = useCookies(['user_id'])
  const [signUpError, setSignUpError] = useState({
    email: '',
    password: '',
    acceptTerms: '',
    server: '',
  })
  const { validateEmail } = useValidator()
  const storage = useStorage()

  const removeError = (errorName) => {
    setSignUpError(
      produce(signUpError, (draftTempSignUpError) => {
        draftTempSignUpError[errorName] = ''
      })
    )
  }

  const removeAllErrors = () => {
    setSignUpError(
      produce(signUpError, (draftTempSignUpError) => {
        Object.keys(signUpError).forEach((key) => {
          draftTempSignUpError[key] = ''
        })
      })
    )
  }

  const validateCredentials = (credentials, isCorrect) => {
    let hasError = false
    const error = {
      email: '',
      password: '',
      acceptTerms: '',
      server: '',
    }

    if (!validateEmail(credentials.email)) {
      error.email = 'Please enter a valid email address'
      hasError = true
    }

    if (credentials.password === '') {
      error.password = 'Please enter a valid password'
      hasError = true
    }

    if (credentials.acceptTerms === false) {
      error.acceptTerms = 'You must accept the terms and conditions'
      hasError = true
    }

    if (!hasError) {
      isCorrect()
    } else {
      setSignUpError(error)
    }
  }

  const signUpWithMicrosoft = async (credentials: any, microsoftAccessToken: string) => {
    try {
      const responseCredentials = await api.signUpWithMicrosoft({
        microsoftAccessToken,
        accept_terms: true, // Always true when calling this function
        attribution: getSignupAttribution() as Record<string, string | undefined>,
      })
      storage.clearNewSession()
      setCookie('user_id', responseCredentials.id, { path: '/' })
      dispatch(setOrganizations(null))
      dispatch(
        setUser({
          id: responseCredentials.id,
          email: responseCredentials.email,
          profile: responseCredentials.profile,
        })
      )
      dispatch(
        setToken({
          accessToken: responseCredentials.access_token,
          accessTokenExpiresAt: responseCredentials.access_token_expires_at,
          userId: responseCredentials.id,
        })
      )
      return responseCredentials
    } catch (error: any) {
      console.log(`Sign Up with Microsoft Error`, error)
      if (isHTTPError(error)) {
        const detailType = (error.response?.data as any)?.detail?.type
        setSignUpError(
          produce(signUpError, (draftTempSignUpError) => {
            draftTempSignUpError.server =
              detailType === 'duplicate_email'
                ? 'An account with this email already exists. Please sign in instead.'
                : 'Microsoft sign up failed. Please try again.'
          })
        )
      }
      return null
    }
  }

  const signUpWithGoogle = async (credentials: any, googleAccessToken: string) => {
    try {
      const responseCredentials = await api.signUpWithGoogle({
        googleAccessToken,
        accept_terms: true, // Always true when calling this function
        attribution: getSignupAttribution() as Record<string, string | undefined>,
      })
      storage.clearNewSession()
      setCookie('user_id', responseCredentials.id, { path: '/' })
      dispatch(setOrganizations(null))
      dispatch(
        setUser({
          id: responseCredentials.id,
          email: responseCredentials.email,
          profile: responseCredentials.profile,
        })
      )
      dispatch(
        setToken({
          accessToken: responseCredentials.access_token,
          accessTokenExpiresAt: responseCredentials.access_token_expires_at,
          userId: responseCredentials.id,
        })
      )
      return responseCredentials
    } catch (error: any) {
      console.log(`Sign Up with Google Error`, error)
      if (isHTTPError(error)) {
        setSignUpError(
          produce(signUpError, (draftTempSignUpError) => {
            draftTempSignUpError.server = error.response?.data?.detail || 'Sign up failed. Please try again.'
          })
        )
      }
      return null
    }
  }

  const signUp = async (credentials: any, handleDuplicateEmail?: () => void) => {
    const request = {
      email: credentials.email,
      password: credentials.password,
      accept_terms: credentials.acceptTerms,
      ...getSignupAttribution(),
    }

    try {
      const response = await api.post('/user', request)
      const data = response.data

      storage.clearNewSession()
      setCookie('user_id', data.id, { path: '/' })
      dispatch(setOrganizations(null))
      dispatch(
        setUser({
          id: data.id,
          email: data.email,
          profile: data.profile,
        })
      )
      dispatch(
        setToken({
          accessToken: data.access_token,
          accessTokenExpiresAt: data.access_token_expires_at,
          userId: data.id,
        })
      )
      return response
    } catch (error: any) {
      if (isHTTPError(error) && error.response.status === 409) {
        setSignUpError(
          produce(signUpError, (draftTempSignUpError) => {
            draftTempSignUpError.email = 'This email address is already in use'
          })
        )
        if (handleDuplicateEmail) {
          handleDuplicateEmail()
        }
      } else if (isHTTPError(error) && error.response.status === 400) {
        const errorData = error.response?.data as any
        const errorType = errorData?.detail?.type
        if (errorType === 'terms_not_accepted') {
          setSignUpError(
            produce(signUpError, (draftTempSignUpError) => {
              draftTempSignUpError.acceptTerms = 'You must accept the terms and conditions'
            })
          )
        } else {
          setSignUpError(
            produce(signUpError, (draftTempSignUpError) => {
              draftTempSignUpError.server = errorData?.detail || 'Sign up failed. Please try again.'
            })
          )
        }
      }
      return null
    }
  }

  return {
    signUpError,
    signUpWithGoogle,
    signUpWithMicrosoft,
    removeError,
    removeAllErrors,
    validateCredentials,
    signUp,
  }
}

export default useSignUp
